import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react'
import { AlertTriangle, Check, History, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { BRAND, OUT_EASE, halftone } from '@/components/landing/service/halftone'

/* ----------------------------------------------------------------------------
   Animated Excel fragment for the finance statement hero's 16:9 media slot:
   a budget-vs-actual sheet with a forecast column and the Templatean pane.
   It cycles through the page's three steps (formula explained, error caught,
   change logged) while on screen. Illustrative figures; decorative.
---------------------------------------------------------------------------- */

const PHASE_MS = 3400
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'

type Row = { item: string; budget: string; actual: string; variance: string; pct: string; forecast: number; total?: boolean }

const ROWS: Row[] = [
  { item: 'Revenue', budget: '4,200', actual: '4,410', variance: '210', pct: '5.0%', forecast: 82 },
  { item: 'Cost of sales', budget: '1,680', actual: '1,790', variance: '-110', pct: '-6.5%', forecast: 46 },
  { item: 'Gross margin', budget: '2,520', actual: '2,620', variance: '100', pct: '4.0%', forecast: 64, total: true },
  { item: 'Operating costs', budget: '1,150', actual: '1,120', variance: '30', pct: '2.6%', forecast: 38 },
  { item: 'Marketing', budget: '340', actual: '365', variance: '-25', pct: '-7.4%', forecast: 22 },
  { item: 'EBITDA', budget: '1,030', actual: '1,135', variance: '105', pct: '10.2%', forecast: 70, total: true },
]

const PHASES = [
  { cell: [0, 3], formula: '=C2-B2', tab: 'Formula' },
  { cell: [5, 1], formula: '=B4-SUM(B5:B5)', tab: 'Checks' },
  { cell: [1, 2], formula: '1790', tab: 'Audit trail' },
] as const

const hair = 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)]'
const muted = 'text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]'

function Pane({ phase }: { phase: number }) {
  return (
    <div className={cn('flex h-full flex-col border-l bg-[var(--color-landing-gray-50)] dark:bg-[#15151d]', hair)}>
      <div className={cn('flex items-center gap-[0.5em] border-b px-[1em] py-[0.75em]', hair)}>
        <span className="font-bold tracking-[-0.01em]">
          templ<span className="text-[var(--color-landing-accent)]">a</span>tean
        </span>
        <span className={cn('ml-auto text-[0.8em]', muted)}>Excel</span>
      </div>
      <div className={cn('grid grid-cols-3 border-b text-[0.78em] font-semibold uppercase tracking-wide', hair)}>
        {PHASES.map((p, i) => (
          <span
            key={p.tab}
            className={cn(
              'relative px-[0.4em] py-[0.7em] text-center transition-colors duration-300',
              i === phase ? 'text-[var(--color-landing-gray-900)] dark:text-white' : muted
            )}
          >
            {p.tab}
            <span
              className={cn('absolute inset-x-0 bottom-0 h-[2px] origin-left bg-[var(--color-landing-accent)] transition-transform duration-500', EASE)}
              style={{ transform: `scaleX(${i === phase ? 1 : 0})` }}
            />
          </span>
        ))}
      </div>
      <div className="relative flex-1 p-[1em]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={phase}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: OUT_EASE }}
            className="flex flex-col gap-[0.8em]"
          >
            {phase === 0 && (
              <>
                <span className="self-start border px-[0.7em] py-[0.5em] text-[0.9em] leading-snug dark:border-[var(--color-landing-gray-700)]">
                  Variance between actual and budget for each line
                </span>
                <span className="bg-[var(--color-landing-primary)] p-[0.9em] text-[0.88em] text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
                  <span className="flex items-center gap-[0.4em] font-semibold">
                    <Sparkles className="h-[1.1em] w-[1.1em]" style={{ color: BRAND.pink }} /> Suggested
                  </span>
                  <span className="mt-[0.4em] block font-[family-name:var(--font-landing-mono)]">=C2-B2</span>
                  <span className="mt-[0.4em] block opacity-80">Actual (C) minus budget (B). Positive means ahead of plan.</span>
                </span>
              </>
            )}
            {phase === 1 && (
              <>
                <span className="flex items-start gap-[0.5em] border p-[0.8em] text-[0.88em] leading-snug" style={{ borderColor: BRAND.pink }}>
                  <AlertTriangle className="mt-[0.1em] h-[1.2em] w-[1.2em] shrink-0" style={{ color: BRAND.pink }} />
                  <span>
                    <span className="block font-semibold">Range check: B7</span>
                    <span className={muted}>SUM covers B5 only, so Marketing (B6) is left out.</span>
                  </span>
                </span>
                <span className="flex items-center gap-[0.5em] text-[0.85em]">
                  <span className="flex h-[1.4em] w-[1.4em] items-center justify-center text-white" style={{ backgroundColor: BRAND.green }}>
                    <Check className="h-[0.95em] w-[0.95em]" strokeWidth={3} />
                  </span>
                  Totals tie to the ledger
                </span>
                <span className="flex items-center gap-[0.5em] text-[0.85em]">
                  <span className="flex h-[1.4em] w-[1.4em] items-center justify-center text-white" style={{ backgroundColor: BRAND.green }}>
                    <Check className="h-[0.95em] w-[0.95em]" strokeWidth={3} />
                  </span>
                  No hard-coded numbers
                </span>
              </>
            )}
            {phase === 2 && (
              <>
                {[
                  ['C3', 'Analyst', 'Actuals loaded'],
                  ['B7', 'Analyst', 'Range fixed'],
                  ['F', 'Controller', 'Forecast approved'],
                ].map(([cell, who, what]) => (
                  <span key={cell} className={cn('grid grid-cols-[auto_1fr] items-start gap-x-[0.6em] border-b pb-[0.6em] text-[0.85em]', hair)}>
                    <History className="mt-[0.1em] h-[1.1em] w-[1.1em] text-[var(--color-landing-secondary)]" />
                    <span>
                      <span className="font-semibold">{what}</span>
                      <span className={cn('block', muted)}>
                        {who} · {cell}
                      </span>
                    </span>
                  </span>
                ))}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

/**
 * The 16:9 media block: a grey stage with a pink halftone block and a framed
 * Excel window. Text sizes use container units so the sheet scales with the
 * block; below md it drops the 16:9 box and the pane stacks under the sheet.
 */
export function VarianceSheet({ title }: { title: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.35 })
  const reduce = useReducedMotion()
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    if (reduce || !inView) return
    const id = window.setInterval(() => setPhase((p) => (p + 1) % PHASES.length), PHASE_MS)
    return () => window.clearInterval(id)
  }, [inView, reduce])

  const current = PHASES[phase]!
  const cols = ['Line item', 'Budget', 'Actual', 'Variance', 'Var %', 'Forecast']

  return (
    <div
      ref={ref}
      aria-hidden
      className="relative overflow-hidden bg-[#f3f3f3] [container-type:inline-size] dark:bg-[#15151d] md:aspect-video"
    >
      <span className="absolute right-0 top-0 h-[55%] w-[38%]" style={{ backgroundColor: BRAND.pink, ...halftone(0.3, 7) }} />
      <span className="absolute bottom-[6%] left-[3%] h-[22%] w-[14%] border border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)]" />

      <div className="relative m-[4%] flex flex-col overflow-hidden bg-white text-[clamp(9px,1.1cqw,15px)] leading-[1.35] text-[var(--color-landing-gray-900)] shadow-[0_40px_80px_-30px_rgba(10,10,10,0.4)] dark:bg-[#1c1c26] dark:text-white md:absolute md:inset-[7%_6%_8%_6%] md:m-0">
        {/* Window bar */}
        <div className={cn('flex items-center gap-[0.6em] border-b px-[1em] py-[0.7em]', hair)}>
          <OfficeAppIcon app="excel" className="h-[1.9em] w-[1.9em]" />
          <span className="font-semibold">{title}.xlsx</span>
          <span className={cn('ml-auto hidden gap-[1.4em] text-[0.85em] sm:flex', muted)}>
            <span>Home</span>
            <span>Formulas</span>
            <span>Data</span>
            <span>Review</span>
          </span>
        </div>
        {/* Formula bar */}
        <div className={cn('flex items-center gap-[0.8em] border-b px-[1em] py-[0.5em] font-[family-name:var(--font-landing-mono)] text-[0.88em]', hair)}>
          <span className={cn('w-[2.6em] border-r pr-[0.6em] font-semibold', hair)}>
            {String.fromCharCode(65 + current.cell[1])}
            {current.cell[0] + 2}
          </span>
          <span className="font-semibold italic text-[var(--color-landing-gray-500)]">fx</span>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={phase}
              className="truncate"
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: OUT_EASE }}
            >
              {current.formula}
            </motion.span>
          </AnimatePresence>
        </div>

        <div className="grid flex-1 md:grid-cols-[minmax(0,1fr)_minmax(0,0.36fr)]">
          {/* Sheet */}
          <div className="p-[0.9em]">
            <div className="grid grid-cols-[1.6em_minmax(0,1.5fr)_repeat(4,minmax(0,1fr))_minmax(0,1.2fr)] text-[0.85em] tabular-nums">
              <span />
              {['A', 'B', 'C', 'D', 'E', 'F'].map((c) => (
                <span key={c} className={cn('border-b border-r py-[0.25em] text-center font-semibold', hair, muted)}>
                  {c}
                </span>
              ))}
              <span className={cn('border-b border-r py-[0.5em] text-center', hair, muted)}>1</span>
              {cols.map((c) => (
                <span key={c} className={cn('truncate border-b border-r bg-[var(--color-landing-gray-50)] px-[0.5em] py-[0.5em] font-semibold dark:bg-[#22222c]', hair)}>
                  {c}
                </span>
              ))}
              {ROWS.map((row, r) => {
                const values = [row.item, row.budget, row.actual, row.variance, row.pct]
                return (
                  <div key={row.item} className="contents">
                    <span className={cn('border-b border-r py-[0.55em] text-center', hair, muted)}>{r + 2}</span>
                    {values.map((v, c) => {
                      const selected = current.cell[0] === r && current.cell[1] === c
                      const flagged = phase === 1 && r === 5 && c === 1
                      const negative = c >= 3 && v.startsWith('-')
                      return (
                        <span
                          key={c}
                          className={cn(
                            'relative truncate border-b border-r px-[0.5em] py-[0.55em] transition-[background-color] duration-300',
                            hair,
                            c > 0 && 'text-right',
                            row.total && 'font-semibold',
                            negative && 'text-[#d4000f] dark:text-[#ff6b75]',
                            selected && 'bg-[rgba(12,0,255,0.07)]',
                            flagged && 'bg-[rgba(255,0,242,0.1)]'
                          )}
                        >
                          {v}
                          {selected && (
                            <motion.span
                              layoutId="sheet-cursor"
                              className="absolute inset-0 border-2"
                              style={{ borderColor: flagged ? BRAND.pink : BRAND.blue }}
                              transition={{ duration: 0.45, ease: OUT_EASE }}
                            />
                          )}
                          {phase === 2 && r === 1 && c === 2 && (
                            <span className="absolute right-0 top-0 h-0 w-0 border-l-[0.6em] border-t-[0.6em] border-l-transparent" style={{ borderTopColor: BRAND.purple }} />
                          )}
                        </span>
                      )
                    })}
                    {/* Forecast: an in-cell bar */}
                    <span className={cn('flex items-center border-b border-r px-[0.5em] py-[0.55em]', hair)}>
                      <span
                        className={cn('block h-[0.7em] origin-left transition-transform duration-700', EASE)}
                        style={{
                          width: `${row.forecast}%`,
                          backgroundColor: row.total ? BRAND.blue : BRAND.purple,
                          transform: `scaleX(${inView || reduce ? 1 : 0})`,
                          transitionDelay: `${r * 70}ms`,
                        }}
                      />
                    </span>
                  </div>
                )
              })}
            </div>
            <div className={cn('mt-[0.9em] flex gap-[0.4em] text-[0.8em]', muted)}>
              {['Variance', 'Forecast', 'Assumptions'].map((tab, i) => (
                <span
                  key={tab}
                  className={cn(
                    'border px-[0.8em] py-[0.25em]',
                    hair,
                    i === 0 && 'border-b-2 border-b-[#107c41] font-semibold text-[var(--color-landing-gray-900)] dark:text-white'
                  )}
                >
                  {tab}
                </span>
              ))}
            </div>
          </div>
          {/* Templatean pane */}
          <div className="min-h-[14em] md:min-h-0">
            <Pane phase={phase} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default VarianceSheet
